import _ from 'lodash';
import React, { useRef, useState, useEffect } from 'react';
import { Notification, Text, Tooltip } from '@wix/design-system';
import { translate } from '#packages/i18n';

import styles from './deprecationOverlayItem.scss';
import { AUTOMATION_IDS, DEPRECATED_COMPONENTS_DATA } from '../constants';

import type { CompRef } from 'types/documentServices';

interface DeprecationOverlayItemProps {
  compRef: CompRef;
  compType: string;
  compLayout: React.CSSProperties;
  selectedComp: CompRef[];
  isDragging: boolean;
  openDeprecationModal: (compType: string) => void;
}

const DEFAULT_BANNER_TITLE_WIDTH = 720;
const LARGE_BANNER_WIDTH_THRESHOLD = 250;
const LARGE_BANNER_WIDTH_SCALE = 0.75;
const SMALL_BANNER_WIDTH_SCALE = 0.5;

/*
 Several workarounds are used in this component, such as disabling Tooltip in Text and using it explicitly
 or managing the visibility of the Tooltip manually. Context:
 1. https://wix.slack.com/archives/C39FTGUBZ/p1739376272536779
 2. https://wix.slack.com/archives/C39FTGUBZ/p1739379674601519
 */
// TODO: Rewrite this component after Text and Notification.TextLabel components are fixed
const DeprecationOverlayItem: React.FC<DeprecationOverlayItemProps> =
  React.memo(
    ({
      compRef,
      compType,
      compLayout,
      selectedComp,
      isDragging,
      openDeprecationModal,
    }) => {
      const textRef = useRef(null);
      const [isTooltipVisible, setTooltipVisible] = useState(false);

      useEffect(() => {
        setTimeout(() => {
          if (textRef.current) {
            setTooltipVisible(shouldShowTooltip());
          }
        }, 0);
      }, []);

      const isOverlayVisible = () => {
        const isCompSelected =
          selectedComp &&
          selectedComp.length > 0 &&
          selectedComp[0].id === compRef.id;

        return !(isCompSelected && isDragging);
      };
      const bannerText =
        DEPRECATED_COMPONENTS_DATA[compType]?.TRANSLATIONS?.BANNER;

      if (!isOverlayVisible()) {
        return null;
      }

      const handleOnClick = () => {
        openDeprecationModal(compType);
      };

      const shouldShowTooltip = () => {
        const { current: containerElement } = textRef;
        const textElement = containerElement?.firstChild;
        const containerWidth = containerElement?.offsetWidth;

        return (
          textElement &&
          ((containerWidth && textElement.scrollWidth - containerWidth > 1) ||
            textElement.offsetWidth < textElement.scrollWidth)
        );
      };

      /*
        In order to fix the hardcoded max-width value of the <Notification.TextLabel> component,
        we wrap the <Text> component with a div and set the max-width value relative to the width
        of the deprecated component.
        If the deprecated component is wider than LARGE_BANNER_WIDTH_THRESHOLD, the title's max-width will be LARGE_BANNER_WIDTH_SCALE % of the component's width.
        Otherwise, the title's max-width will beSMALL_BANNER_WIDTH_SCALE %.
      */
      const calculateBannerTitleMaxWidth = (
        width: React.CSSProperties['width'],
      ) => {
        if (typeof width === 'number') {
          const scale =
            width > LARGE_BANNER_WIDTH_THRESHOLD
              ? LARGE_BANNER_WIDTH_SCALE
              : SMALL_BANNER_WIDTH_SCALE;

          return `${Math.floor(width * scale)}px`;
        }

        // by default setting to the value of the <Notification.TextLabel>'s max-width container. Details can be found by the links in the top comment
        return `${DEFAULT_BANNER_TITLE_WIDTH}px`;
      };

      const getBannerTitle = () => {
        return (
          <>
            {translate(bannerText?.TEXT)}{' '}
            <a onClick={handleOnClick} className={styles.bannerLink}>
              {translate(bannerText?.CTA)}
            </a>
          </>
        );
      };

      const renderText = () => {
        return (
          <div
            ref={textRef}
            style={{
              maxWidth: calculateBannerTitleMaxWidth(compLayout.width),
            }}
          >
            <Text ellipsis light showTooltip={false}>
              {getBannerTitle()}
            </Text>
          </div>
        );
      };

      return (
        <div
          className={styles.deprecationBox}
          style={compLayout}
          data-comp-id={`deprecationOverlay-${compRef.id}`}
          data-hook={AUTOMATION_IDS.DEPRECATION_OVERLAY}
        >
          <Notification zIndex={1} type="local" theme="error" show>
            <Notification.TextLabel showTooltip={false}>
              {isTooltipVisible ? (
                <Tooltip
                  moveBy={{ y: 8 }}
                  content={
                    <Text size="small" light>
                      {getBannerTitle()}
                    </Text>
                  }
                >
                  {renderText()}
                </Tooltip>
              ) : (
                renderText()
              )}
            </Notification.TextLabel>
          </Notification>
          <div className={styles.deprecationOverlay}>
            <div className={styles.deprecationPattern} />
          </div>
        </div>
      );
    },
    (prevProps, nextProps) => {
      return (
        prevProps.compRef.id === nextProps.compRef.id &&
        prevProps.isDragging === nextProps.isDragging &&
        prevProps.selectedComp === nextProps.selectedComp &&
        _.isEqual(prevProps.compLayout, nextProps.compLayout)
      );
    },
  );

DeprecationOverlayItem.displayName = 'DeprecationOverlayItem';

export default DeprecationOverlayItem;
